/**
 * @file src/components/UserTable/columns.ts
 * @exports emailColumn, getUserTableColumns, nameColumn, orgsColumn, roleColumn, roleFilters, visibleRoleFilters
 * @summary Defines user table columns and the shared role filter options used by both user filter controls.
 */
import { ColumnType, TextStyleEnum } from "@cerebruminc/cerebellum";
import { RoleEnum } from "src/const";
import { isUserRoleVisible } from "src/helpers/userPermissions";
import { RoleCell, OrgCell, NameCell } from "./componentWrappers";
import { rolePrettyNameLib } from "./helpers";
import { UserTableSortEnum, UserTableFilterIds } from "./types";
import { ExtendedTooltipType, FiltersObjectType } from "src/views/types";
import { OrganizationRes } from "src/generated/graphql";

export const emailColumn: ColumnType = {
	cellId: UserTableSortEnum.EMAIL,
	label: "Email",
	minWidth: 200,
	textStyle: TextStyleEnum.Tertiary,
	width: 10,
};

export const nameColumn: ColumnType = {
	cellId: UserTableSortEnum.FIRST_NAME,
	label: "Full Name",
	textStyle: TextStyleEnum.Tertiary,
	width: 10,
	minWidth: 240,
	disableSort: true,
	ComponentWrapper: NameCell,
};

export const roleFilters = Object.values(RoleEnum).map((role) => ({
	id: role,
	label: rolePrettyNameLib(role),
	value: role,
}));

/** Role filter choices the viewer is allowed to know about. */
export const visibleRoleFilters = ({
	viewerBelongsToCra,
	viewerRole,
	viewerRoles,
}: {
	viewerBelongsToCra?: boolean;
	viewerRole?: string | null;
	viewerRoles?: Iterable<string> | null;
}) =>
	roleFilters.filter((role) =>
		isUserRoleVisible({ roleName: role.value, viewerBelongsToCra, viewerRole, viewerRoles }),
	);

export const roleColumn: ColumnType = {
	cellId: UserTableFilterIds.Role,
	disableSort: true,
	fixedWidth: true,
	label: "Role",
	textStyle: TextStyleEnum.Secondary,
	width: 230,
	ComponentWrapper: RoleCell,
	filters: roleFilters,
};

export const orgsColumn = (organizations: Pick<OrganizationRes, "id" | "name">[] = []): ColumnType => ({
	cellId: UserTableFilterIds.Organization,
	disableSort: true,
	fixedWidth: true,
	label: "Organizations",
	textStyle: TextStyleEnum.Secondary,
	width: 250,
	ComponentWrapper: OrgCell,
	filters: organizations.map((org) => ({
		id: org?.id ?? "",
		label: org?.name ?? "",
		name: org?.name ?? "",
	})),
});

interface GetUserTableColumnsProps {
	filters: FiltersObjectType;
	handleMouseEnteredRow?: (tooltipOptions: ExtendedTooltipType) => void;
	handleMouseExitedRow?: () => void;
}

export const getUserTableColumns = ({ filters }: GetUserTableColumnsProps): ColumnType[] => {
	const columns: ColumnType[] = [
		nameColumn,
		emailColumn,
		{
			cellId: UserTableFilterIds.Role,
			disableSort: true,
			fixedWidth: true,
			label: "Role",
			textStyle: TextStyleEnum.Secondary,
			width: 230,
			ComponentWrapper: RoleCell,
			filters: filters.roles.map((role) => ({
				id: role.id,
				label: role.label,
			})),
		},
		{
			cellId: UserTableFilterIds.Organization,
			disableSort: true,
			fixedWidth: true,
			label: "Organizations",
			textStyle: TextStyleEnum.Secondary,
			width: 250,
			ComponentWrapper: OrgCell,
			filters: filters.organizations?.map((org) => ({
				id: org.id,
				label: org.label,
			})),
		},
	];
	return columns;
};
